<script setup lang="ts">
import type { Content } from '@prismicio/client'
import { PLANNING_SLOTS } from '~/composables/useOfferteWizard'

const {
  pkg,
  details,
  rooms,
  planningSlots,
  planningNoPreference,
  totalM2,
  consent,
  fieldErrors,
  recommendedPackage,
  goTo,
  persist,
} = useOfferteWizard()

const slotLookup = new Map(PLANNING_SLOTS.map((s) => [s.id, s]))

const planningRows = computed(() => {
  const map = new Map<string, string[]>()
  for (const key of planningSlots.value) {
    const [day, slotId] = key.split(':')
    if (!day || !slotId) continue
    const arr = map.get(day) ?? []
    arr.push(slotId)
    map.set(day, arr)
  }
  return Array.from(map.entries()).map(([day, ids]) => ({
    day,
    labels: ids
      .map((id) => slotLookup.get(id)?.label || id)
      .join(' + '),
  }))
})

const { client } = usePrismic()

const { data: chosenPackage } = await useAsyncData<Content.AircoPackageDocument | null>(
  () => `wizard-chosen-${pkg.value}`,
  async () => {
    if (!pkg.value || pkg.value === 'advice') return null
    try {
      return await client.getByUID<Content.AircoPackageDocument>('airco_package', pkg.value)
    } catch {
      return null
    }
  },
  { watch: [pkg] },
)

const priceFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})
const formatPrice = (v: number | null | undefined) =>
  typeof v === 'number' ? priceFormatter.format(v) : ''

function onConsentChange(e: Event) {
  consent.value = (e.target as HTMLInputElement).checked
  if (consent.value && fieldErrors.value.consent) {
    const next = { ...fieldErrors.value }
    delete next.consent
    fieldErrors.value = next
  }
  persist()
}
</script>

<template>
  <section class="pa-wizard__panel">
    <div class="pa-wizard__panel-head">
      <h2>Controleer & verstuur</h2>
      <p>Klopt alles? Verstuur je aanvraag en we nemen binnen 2 werkdagen contact met je op voor de definitieve offerte.</p>
    </div>

    <div class="pa-wizard__summary">
      <!-- Block 1: Package -->
      <div class="pa-wizard__sum-block">
        <div class="pa-wizard__sb-head">
          <span class="pa-wizard__sb-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m9 12 2 2 4-4" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <h3>Gekozen pakket</h3>
          <button type="button" class="pa-wizard__sb-edit" @click="goTo(1)">Wijzigen</button>
        </div>
        <div class="pa-wizard__sb-body">
          <div v-if="pkg === 'advice'" class="pa-wizard__sum-pkg">
            <span class="pa-wizard__sum-pkg-name">Persoonlijk advies gewenst</span>
          </div>
          <div v-else-if="chosenPackage" class="pa-wizard__sum-pkg">
            <span class="pa-wizard__sum-pkg-name">{{ chosenPackage.data.title }}</span>
            <span v-if="typeof chosenPackage.data.from_price === 'number'" class="pa-wizard__sum-pkg-price">
              {{ formatPrice(chosenPackage.data.from_price) }} <small>vanaf</small>
            </span>
          </div>
          <div v-else class="pa-wizard__sum-pkg">
            <span class="pa-wizard__sum-pkg-name pa-wizard__sum-pkg-empty">Nog geen pakket gekozen</span>
          </div>

          <div v-if="pkg === 'advice' && recommendedPackage" class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Onze suggestie o.b.v. {{ totalM2 }} m²</span>
            <span class="pa-wizard__sum-v">
              {{ recommendedPackage.data.title }}
              <template v-if="typeof recommendedPackage.data.from_price === 'number'">
                · {{ formatPrice(recommendedPackage.data.from_price) }}
              </template>
            </span>
          </div>
        </div>
      </div>

      <!-- Block 2: Details -->
      <div class="pa-wizard__sum-block">
        <div class="pa-wizard__sb-head">
          <span class="pa-wizard__sb-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <h3>Je gegevens</h3>
          <button type="button" class="pa-wizard__sb-edit" @click="goTo(2)">Wijzigen</button>
        </div>
        <div class="pa-wizard__sb-body">
          <div class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Naam</span>
            <span class="pa-wizard__sum-v">{{ `${details.first_name} ${details.last_name}`.trim() || '—' }}</span>
          </div>
          <div class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">E-mail</span>
            <span class="pa-wizard__sum-v">{{ details.email || '—' }}</span>
          </div>
          <div class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Telefoon</span>
            <span class="pa-wizard__sum-v">{{ details.phone || '—' }}</span>
          </div>
          <div class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Adres</span>
            <span class="pa-wizard__sum-v">
              <template v-if="details.street">
                {{ details.street }} {{ details.house_number }}<template v-if="details.house_addition"> {{ details.house_addition }}</template><br>
                {{ details.postcode }}<template v-if="details.city"> {{ details.city }}</template>
              </template>
              <template v-else>
                {{ [details.postcode, details.house_number].filter(Boolean).join(' ') || '—' }}<template v-if="details.city">, {{ details.city }}</template>
              </template>
            </span>
          </div>
          <div class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Parkeren</span>
            <span class="pa-wizard__sum-v">{{ details.parking || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Block 3: Rooms -->
      <div class="pa-wizard__sum-block">
        <div class="pa-wizard__sb-head">
          <span class="pa-wizard__sb-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 9.5 12 3l9 6.5" />
              <path d="M5 8.5V21h14V8.5" />
              <path d="M9 21v-6h6v6" />
            </svg>
          </span>
          <h3>Woningconfiguratie</h3>
          <button type="button" class="pa-wizard__sb-edit" @click="goTo(3)">Wijzigen</button>
        </div>
        <div class="pa-wizard__sb-body">
          <ul v-if="rooms.length" class="pa-wizard__sum-rooms">
            <li v-for="room in rooms" :key="room.id">
              <span class="pa-wizard__sum-rn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9.5 12 3l9 6.5" /><path d="M5 8.5V21h14V8.5" /><path d="M9 21v-6h6v6" />
                </svg>
                <span>
                  {{ room.name ? `${room.name} · ${room.type}` : room.type }}
                  <em v-if="room.floor" class="pa-wizard__sum-rn-meta">{{ room.floor }}</em>
                </span>
              </span>
              <span class="pa-wizard__sum-rm">{{ room.m2 || 0 }} m²</span>
            </li>
          </ul>
          <div v-else class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Geen ruimtes toegevoegd</span>
          </div>
          <div v-if="rooms.length" class="pa-wizard__sum-total">
            <span>Totaal</span>
            <span>{{ rooms.length }} ruimtes · {{ totalM2 }} m²</span>
          </div>
        </div>
      </div>

      <!-- Block 4: Planning -->
      <div class="pa-wizard__sum-block">
        <div class="pa-wizard__sb-head">
          <span class="pa-wizard__sb-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <h3>Beschikbaarheid</h3>
          <button type="button" class="pa-wizard__sb-edit" @click="goTo(4)">Wijzigen</button>
        </div>
        <div class="pa-wizard__sb-body">
          <div v-if="planningNoPreference" class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Geen voorkeur</span>
            <span class="pa-wizard__sum-v">Wij stemmen het moment met je af</span>
          </div>
          <template v-else-if="planningRows.length">
            <div
                v-for="row in planningRows"
                :key="row.day"
                class="pa-wizard__sum-row"
            >
              <span class="pa-wizard__sum-k">{{ row.day }}</span>
              <span class="pa-wizard__sum-v">{{ row.labels }}</span>
            </div>
          </template>
          <div v-else class="pa-wizard__sum-row">
            <span class="pa-wizard__sum-k">Nog geen voorkeur opgegeven</span>
          </div>
        </div>
      </div>
    </div>

    <label class="pa-wizard__consent" :class="{ 'pa-wizard__consent--invalid': fieldErrors.consent }">
      <input
          type="checkbox"
          :checked="consent"
          @change="onConsentChange"
      >
      <span>Ik ga ermee akkoord dat Pro-Air contact met mij opneemt over deze aanvraag.</span>
    </label>
    <p v-if="fieldErrors.consent" class="pa-wizard__error pa-wizard__error--block">
      {{ fieldErrors.consent }}
    </p>
  </section>
</template>
